
import React, { useEffect, useState } from 'react';
import { TranslationComponentProps } from './types';
import { translationGlobalConfig } from './TranslationGlobalData';
import { translationService } from './translationService';
import { fanfanloI18n } from '../i18n/fanfanloI18n';
import { useProxyWatch } from '../watcher';
import { useTranslation } from '../i18n';
import { Log } from '../log';
import { Box } from '@mui/material';

// 文件级别log
const fileLog = new Log(false, 'TranslationComponent_file');
fileLog.pause = true;

/**
 * 翻译组件
 * - 根据translationGlobalConfig.auto决定是否自动翻译
 * - 点击可切换原文/译文
 * - 支持HTML内容翻译
 */
export function TranslationComponent(props: TranslationComponentProps) {
  const uiLog = fileLog.sub(false, 'TranslationComponent_ui');
  uiLog.pause = true;

  const { children, sourceLang = 'auto', isHtml = false, className = '' } = props;

  // 监听全局auto配置
  const [auto] = useProxyWatch(translationGlobalConfig, 'auto', translationGlobalConfig.auto);

  // 使用i18n
  const { t } = useTranslation('fanfanlo/translation/content');

  // 组件内部状态
  const [originalText, setOriginalText] = useState<string>('');
  const [translatedText, setTranslatedText] = useState<string>('');
  const [showTranslation, setShowTranslation] = useState<boolean>(false);
  const [isTranslating, setIsTranslating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // 提取文本内容
  useEffect(() => {
    const effectLog = uiLog.sub(false, 'effect_extractText_fn');
    effectLog.pause = true;

    let textContent = '';

    if (typeof children === 'string') {
      textContent = children;
      effectLog.log('extracted text from string', textContent);
    } else if (React.isValidElement(children)) {
      if (isHtml) {
        // 当 isHtml=true 时,将React元素转换为HTML字符串
        textContent = reactElementToHtml(children);
        effectLog.log('converted react element to html', textContent);
      } else {
        // 提取纯文本
        textContent = extractTextFromReactNode(children);
        effectLog.log('extracted text from react element', textContent);
      }
    } else if (Array.isArray(children)) {
      if (isHtml) {
        // 处理数组形式的React元素
        textContent = children.map(child => {
          if (React.isValidElement(child)) {
            return reactElementToHtml(child);
          } else {
            return String(child);
          }
        }).join('');
        effectLog.log('converted react element array to html', textContent);
      } else {
        textContent = children.map(child => {
          if (React.isValidElement(child)) {
            return extractTextFromReactNode(child);
          } else {
            return String(child);
          }
        }).join('');
        effectLog.log('extracted text from array', textContent);
      }
    } else {
      textContent = String(children);
      effectLog.log('converted to string', textContent);
    }

    effectLog.log('final extracted text', textContent);
    setOriginalText(textContent);
  }, [children, isHtml]);

  // 自动翻译逻辑
  useEffect(() => {
    const effectLog = uiLog.sub(false, 'effect_autoTranslate_fn');
    effectLog.pause = true;

    effectLog.log('auto translate check', { auto, originalText });

    if (!auto) {
      effectLog.log('auto is false, reset translation state');
      // 重置翻译状态,恢复显示原文
      setShowTranslation(false);
      setIsTranslating(false);
      setError(null);
      return;
    }

    if (!originalText) {
      effectLog.log('no original text, skip translation');
      return;
    }

    const targetLang = fanfanloI18n.language;
    effectLog.log('target language', targetLang);

    // 如果源语言和目标语言相同,不翻译
    if (sourceLang !== 'auto' && sourceLang === targetLang) {
      effectLog.log('source and target language are the same, skip translation');
      return;
    }

    // 执行翻译
    performTranslation();

    async function performTranslation() {
      const fnLog = effectLog.sub(false, 'performTranslation_fn');
      fnLog.pause = true;

      fnLog.log('start translation', { originalText, sourceLang, targetLang });
      setIsTranslating(true);
      setError(null);

      try {
        const result = await translationService.translateText(
          originalText,
          sourceLang,
          targetLang,
          isHtml
        );

        fnLog.log('translation result', result);

        // 如果检测到的源语言和目标语言相同,不显示翻译
        if (result.from === result.to) {
          fnLog.log('detected source language equals target language, skip translation');
          setShowTranslation(false);
        } else {
          setTranslatedText(result.text);
          setShowTranslation(true);
        }
      } catch (err) {
        fnLog.error('translation error', err);
        setError(err instanceof Error ? err.message : 'Translation failed');
      } finally {
        setIsTranslating(false);
      }
    }
  }, [auto, originalText, sourceLang, isHtml]);

  // 点击切换原文/译文
  const handleClick = () => {
    const fnLog = uiLog.sub(false, 'handleClick_fn');
    fnLog.pause = true;

    if (!translatedText) {
      fnLog.log('no translated text, skip toggle');
      return;
    }

    const newShowTranslation = !showTranslation;
    fnLog.log('toggle show translation', { from: showTranslation, to: newShowTranslation });
    setShowTranslation(newShowTranslation);
  };

  // 渲染内容
  const displayText = showTranslation ? translatedText : originalText;

  uiLog.log('render', { displayText, showTranslation, isTranslating, error });

  return (
    <Box
      data-testid="TranslationComponent__clickBox__01JFXYZAQ6Q7R8S9T0U1V2W3"
      className={className}
      onClick={handleClick}
      sx={{
        cursor: translatedText ? 'pointer' : 'default',
        position: 'relative',
      }}
    >
      {isHtml ? (
        <div dangerouslySetInnerHTML={{ __html: displayText }} />
      ) : (
        <span>{displayText}</span>
      )}

      {isTranslating && (
        <span style={{ marginLeft: '8px', fontSize: '0.8em', color: '#999' }}>
          {t('TranslationComponent.translating')}
        </span>
      )}

      {error && (
        <span style={{ marginLeft: '8px', fontSize: '0.8em', color: 'red' }}>
          {t('TranslationComponent.translationFailed')}
        </span>
      )}
    </Box>
  );
}

/**
 * 从React节点提取文本内容
 */
function extractTextFromReactNode(node: React.ReactNode): string {
  const fnLog = fileLog.sub(false, 'extractTextFromReactNode_fn');
  fnLog.pause = true;

  if (typeof node === 'string') {
    return node;
  }

  if (typeof node === 'number') {
    return String(node);
  }

  if (React.isValidElement(node)) {
    if (node.props.children) {
      if (Array.isArray(node.props.children)) {
        return node.props.children.map(extractTextFromReactNode).join('');
      } else {
        return extractTextFromReactNode(node.props.children);
      }
    }
  }

  if (Array.isArray(node)) {
    return node.map(extractTextFromReactNode).join('');
  }

  fnLog.log('unknown node type', node);
  return '';
}

/**
 * 将React元素转换为HTML字符串
 */
function reactElementToHtml(element: React.ReactElement): string {
  const fnLog = fileLog.sub(false, 'reactElementToHtml_fn');
  fnLog.pause = true;

  if (!React.isValidElement(element)) {
    fnLog.log('not a valid react element');
    return String(element);
  }

  const type = element.type;
  const props = element.props;
  const children = props.children;

  fnLog.log('converting element', { type, props });

  // 处理字符串类型的标签 (如 'div', 'h1', 'p' 等)
  if (typeof type === 'string') {
    const tagName = type;
    const attributes: string[] = [];

    // 构建属性
    for (const [key, value] of Object.entries(props)) {
      if (key === 'children') {
        continue;
      }

      if (key === 'className') {
        attributes.push(`class="${value}"`);
      } else if (key === 'htmlFor') {
        attributes.push(`for="${value}"`);
      } else if (typeof value === 'string') {
        attributes.push(`${key}="${value}"`);
      } else if (typeof value === 'number') {
        attributes.push(`${key}="${value}"`);
      } else if (typeof value === 'boolean' && value) {
        attributes.push(key);
      }
    }

    const attrsString = attributes.length > 0 ? ' ' + attributes.join(' ') : '';

    // 自闭合标签
    const selfClosingTags = ['img', 'br', 'hr', 'input', 'meta', 'link'];
    if (selfClosingTags.includes(tagName)) {
      return `<${tagName}${attrsString} />`;
    }

    // 处理子元素
    let childrenHtml = '';
    if (children !== undefined && children !== null) {
      if (Array.isArray(children)) {
        childrenHtml = children.map(child => {
          if (React.isValidElement(child)) {
            return reactElementToHtml(child);
          } else if (typeof child === 'string') {
            return child;
          } else if (typeof child === 'number') {
            return String(child);
          } else {
            return '';
          }
        }).join('');
      } else if (React.isValidElement(children)) {
        childrenHtml = reactElementToHtml(children);
      } else if (typeof children === 'string') {
        childrenHtml = children;
      } else if (typeof children === 'number') {
        childrenHtml = String(children);
      }
    }

    const html = `<${tagName}${attrsString}>${childrenHtml}</${tagName}>`;
    fnLog.log('generated html', html);
    return html;
  } else {
    // 处理组件类型 (函数组件或类组件)
    fnLog.log('component type, extracting children');
    if (children) {
      if (Array.isArray(children)) {
        return children.map(child => {
          if (React.isValidElement(child)) {
            return reactElementToHtml(child);
          } else {
            return String(child);
          }
        }).join('');
      } else if (React.isValidElement(children)) {
        return reactElementToHtml(children);
      } else {
        return String(children);
      }
    }
    return '';
  }
}
